import Phaser from 'phaser';
import { HAPTICS, MAX_SIM_STEPS_PER_FRAME, PALETTE, SIM_STEP, SPRINT } from '../../config';
import { createAthleteView, type AthleteView } from '../../game/AthleteView';
import { AudioEngine } from '../../game/AudioEngine';
import { t } from '../../game/i18n';
import { getDiscipline } from '../../game/data/disciplines';
import { getSkin } from '../../game/data/skins';
import { countryFlag } from '../../game/data/flags';
import { pickOpponents } from '../../game/data/aiRoster';
import type { DisciplineResult, Standing } from '../../game/types';
import { Button } from '../../ui/Button';
import { PowerMeter } from '../../ui/PowerMeter';
import { TapZones } from '../../ui/TapZones';
import { viewport } from '../../ui/viewport';
import { DisciplineScene } from './DisciplineScene';

type Side = 'L' | 'R';
type Phase = 'intro' | 'ready' | 'swim' | 'done';

const START_X = 150;
const RACE_M = 50;
const PPM = SPRINT.pixelsPerMeter;

interface Ai { name: string; species: string; nationality: string; target: number; pos: number; }

/**
 * 50m Freestyle — first nail the DIVE (stop the power bar high for a head-start), then mash
 * SX/DX in rhythm to swim. Rivals race on the progress bar up top. Fastest time wins; beat
 * your own best for a record. Time-based.
 */
export class Swim50m extends DisciplineScene {
  private phase: Phase = 'intro';
  private rig!: AthleteView;

  private velocity = 0;
  private dist = 0;
  private acc = 0;
  private lastSide: Side | null = null;
  private cadence = 0;
  private lastTapAt = 0;
  private maxSpeed: number = SPRINT.maxSpeed;
  private impulseScale = 1;
  private divePower = 0;
  private elapsed = 0;
  private wrTime = 0;
  private wrLine!: Phaser.GameObjects.Graphics;

  private W = 0; private H = 0; private waterY = 0; private uiS = 1; private camTarget = 0;
  private ai: Ai[] = [];

  private diveBar!: PowerMeter;
  private speedBar!: PowerMeter;
  private hud!: Phaser.GameObjects.Text;
  private bigText!: Phaser.GameObjects.Text;
  private diveBtn!: Button;
  private tapZones!: TapZones;
  private progress!: Phaser.GameObjects.Graphics;

  constructor() { super('Swim50m'); }

  create(): void {
    super.create();
    this.phase = 'intro';
    this.velocity = 0; this.dist = 0; this.acc = 0; this.cadence = 0; this.lastSide = null; this.divePower = 0; this.elapsed = 0; this.camTarget = 0;

    const { w, h, s } = viewport(this);
    this.W = w; this.H = h; this.uiS = s;
    this.waterY = h - Math.max(120, h * 0.34);
    const speedFactor = 0.7 + (this.athlete.stats.speed / 100) * 0.6;
    this.maxSpeed = SPRINT.maxSpeed * speedFactor;
    this.impulseScale = speedFactor;

    this.drawPool();
    this.rig = createAthleteView(this, START_X, this.waterY, this.athlete, Phaser.Math.Clamp(h * 0.22, 110, 200));
    this.rig.container.setDepth(6);
    this.progress = this.add.graphics().setScrollFactor(0).setDepth(40);
    this.wrLine = this.add.graphics().setDepth(4);
    this.fetchWorldRecord('swim_50m', 'time', (m) => { this.wrTime = m; });

    const opponents = pickOpponents(getSkin(this.athlete.skin).id, 3);
    this.ai = opponents.map((o) => ({ name: o.name, species: o.species, nationality: o.nationality, target: Math.max(24, 30 + (o.sprintTime - 11) * 1.6 + (Math.random() * 4 - 2)), pos: 0 }));

    this.buildHud();
    this.controls.on('tapLeft', () => this.onTap('L'));
    this.controls.on('tapRight', () => this.onTap('R'));
    this.controls.on('action', () => this.dive());
    this.startCountdown();
  }

  private drawPool(): void {
    const endX = START_X + RACE_M * PPM + 80;
    const g = this.add.graphics().setDepth(-10);
    g.fillStyle(0x0a2a55, 1).fillRect(0, 0, endX + this.W, this.H);
    g.fillStyle(0x1f6fb2, 1).fillRect(0, this.waterY - 40, endX + this.W, this.H - this.waterY + 40);
    g.fillStyle(0x9fd3ff, 0.35);
    for (let yy = this.waterY + 18; yy < this.H; yy += 46) g.fillRect(0, yy, endX + this.W, 3);
    for (let m = 5; m <= RACE_M; m += 5) {
      const x = START_X + m * PPM;
      g.fillStyle(0xffffff, 0.25).fillRect(x, this.waterY - 30, 2, this.H - this.waterY + 30);
    }
    g.fillStyle(PALETTE.bad, 1).fillRect(START_X + RACE_M * PPM, this.waterY - 34, 5, this.H - this.waterY + 34);
    this.add.text(START_X + RACE_M * PPM, this.waterY - 44, t('hud.finishLine'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: '14px', color: '#ffd9a8', fontStyle: 'bold' }).setOrigin(0.5, 1).setDepth(-9);
    this.cameras.main.setBounds(0, 0, endX + this.W, this.H);
  }

  private buildHud(): void {
    const fs = (n: number) => `${Math.max(13, Math.round(n * this.uiS))}px`;
    this.hud = this.add.text(this.W / 2, this.H - Math.max(70, 78 * this.uiS), t('game.hudSwimReady'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(22), color: '#f4f7ff', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 3, '#000000aa', 5);
    const barW = Math.min(300, this.W * 0.5);
    const topY = Math.max(64, this.H * 0.18);
    this.diveBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18, fillColor: PALETTE.power });
    this.add.text(this.W / 2, topY - 18, t('hud.dive'), { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(12), color: '#d3dbf2' }).setOrigin(0.5).setScrollFactor(0);
    this.speedBar = new PowerMeter(this, this.W / 2, topY, { width: barW, height: 18 });
    this.speedBar.setVisible(false);
    this.bigText = this.add.text(this.W / 2, this.H * 0.42, '', { fontFamily: '"Twemoji Country Flags", system-ui', fontSize: fs(78), color: '#ffd24a', fontStyle: 'bold' })
      .setOrigin(0.5).setScrollFactor(0).setShadow(0, 8, '#00000088', 12);
    const label = this.sys.game.device.os.desktop ? `🤿 ${t('btn.swimDive')} ${t('btn.space')}` : `🤿 ${t('btn.swimDive')}`;
    this.diveBtn = new Button(this, this.W / 2, this.H - Math.max(30, 38 * this.uiS), label, () => this.dive(), {
      width: Math.min(200, this.W * 0.4), height: Math.max(52, 58 * this.uiS), fontSize: Math.max(18, Math.round(22 * this.uiS)), color: PALETTE.power,
    });
    this.diveBtn.setScrollFactor(0).setAlpha(0.92).setVisible(false);
    this.tapZones = new TapZones(this);
    this.tapZones.setVisible(false);
  }

  private startCountdown(): void {
    ['3', '2', '1', t('game.go')].forEach((label, i) => {
      this.time.delayedCall(i * 600, () => {
        this.bigText.setText(label).setAlpha(1).setScale(1.5);
        this.tweens.add({ targets: this.bigText, scale: 1, duration: 300, ease: 'Back.out' });
        if (label === t('game.go')) {
          AudioEngine.go();
          this.phase = 'ready';
          this.controls.setEnabled(true);
          this.diveBtn.setVisible(true);
          this.diveBar.setVisible(true);
          this.hud.setText(t('game.stopDive'));
          this.tweens.add({ targets: this.bigText, alpha: 0, delay: 250, duration: 200 });
        } else AudioEngine.countdownTick();
      });
    });
  }

  private dive(): void {
    if (this.phase !== 'ready') return;
    this.velocity = 2 + this.divePower * 4.5; // head start from the dive power
    this.phase = 'swim';
    this.diveBar.setVisible(false);
    this.diveBtn.setVisible(false);
    this.speedBar.setVisible(true);
    this.tapZones.setVisible(true);
    this.lastTapAt = this.time.now / 1000;
    this.hud.setText(t('game.swimMash'));
    this.rig.setPose('run');
    AudioEngine.go();
    this.vibrate(HAPTICS.goodTap);
  }

  private onTap(side: Side): void {
    if (this.phase !== 'swim') return;
    const now = this.time.now / 1000;
    const dt = Math.max(0.001, now - this.lastTapAt);
    this.cadence = Phaser.Math.Linear(this.cadence, 1 / dt, 0.45);
    this.lastTapAt = now;
    const alternated = side !== this.lastSide;
    this.lastSide = side;
    let mult = alternated ? 1 : SPRINT.sameSidePenalty;
    if (this.cadence >= SPRINT.idealCadenceMin && this.cadence <= SPRINT.idealCadenceMax && alternated) mult *= SPRINT.flowBonus;
    else if (this.cadence > SPRINT.overspinThreshold) mult *= SPRINT.overspinPenalty;
    this.velocity = Math.min(this.maxSpeed * 1.05, this.velocity + SPRINT.tapImpulse * this.impulseScale * mult);
    this.tapZones.flash(side);
    this.splash();
    AudioEngine.tap(alternated ? 0.6 : 0.2);
    this.vibrate(HAPTICS.tap);
  }

  private splash(): void {
    this.add.particles(this.rig.container.x, this.waterY, 'spark', {
      lifespan: 360, speed: { min: 30, max: 110 }, angle: { min: 200, max: 340 },
      scale: { start: 0.4, end: 0 }, alpha: { start: 0.5, end: 0 }, tint: 0xbfe6ff, gravityY: 300, emitting: false,
    }).setDepth(5).explode(5);
  }

  update(_t: number, delta: number): void {
    const dt = delta / 1000;
    this.cameras.main.scrollX = Phaser.Math.Linear(this.cameras.main.scrollX, this.camTarget, 0.12);
    this.rig.update(dt);
    if (this.phase === 'ready') {
      this.divePower = 0.5 + 0.5 * Math.sin(this.time.now / 1000 * 3.4 - Math.PI / 2);
      this.diveBar.set(this.divePower);
    } else if (this.phase === 'swim') {
      this.updateSwim(dt);
    }
  }

  private updateSwim(dt: number): void {
    this.elapsed += dt;
    this.acc += dt;
    let steps = 0;
    while (this.acc >= SIM_STEP && steps < MAX_SIM_STEPS_PER_FRAME) {
      this.velocity *= SPRINT.dragPerStep;
      this.dist += this.velocity * SIM_STEP;
      this.acc -= SIM_STEP; steps++;
    }
    const worldX = START_X + this.dist * PPM;
    this.rig.container.x = worldX;
    const sn = Phaser.Math.Clamp(this.velocity / this.maxSpeed, 0, 1);
    this.rig.setMotion(sn);
    this.speedBar.set(sn);
    this.tapZones.update(dt);
    this.camTarget = Phaser.Math.Clamp(worldX - this.W * 0.3, 0, this.cameras.main.getBounds().width - this.W);
    for (const a of this.ai) a.pos = Math.min(1, a.pos + dt / a.target);
    this.drawProgress();
    this.drawWrLine();
    if (this.time.now / 1000 - this.lastTapAt > SPRINT.cadenceWindow) this.cadence = Phaser.Math.Linear(this.cadence, 0, 0.08);
    if (this.dist >= RACE_M) this.finish();
  }

  /** A faint gold line that advances at the world-record pace — beat it to the wall. */
  private drawWrLine(): void {
    const g = this.wrLine;
    g.clear();
    if (this.wrTime <= 0) return;
    const p = Math.min(1, this.elapsed / this.wrTime);
    const x = START_X + RACE_M * PPM * p;
    g.fillStyle(PALETTE.sun, 0.5).fillRect(x - 1.5, this.waterY - 36, 3, this.H - this.waterY + 36);
    g.fillStyle(PALETTE.sun, 0.9).fillTriangle(x - 8, this.waterY - 40, x + 8, this.waterY - 40, x, this.waterY - 30);
  }

  private drawProgress(): void {
    const g = this.progress;
    g.clear();
    const x0 = this.W * 0.12, x1 = this.W * 0.88, y = Math.max(28, this.H * 0.07);
    g.fillStyle(PALETTE.ink, 0.45).fillRoundedRect(x0 - 8, y - 10, x1 - x0 + 16, 20, 10);
    g.fillStyle(PALETTE.bad, 0.8).fillRect(x1, y - 10, 3, 20);
    const dot = (p: number, color: number, big: boolean): void => {
      g.fillStyle(color, 1).fillCircle(x0 + (x1 - x0) * Phaser.Math.Clamp(p, 0, 1), y, big ? 7 : 5);
    };
    this.ai.forEach((a) => dot(a.pos, PALETTE.cadence, false));
    dot(this.dist / RACE_M, PALETTE.laneAccent, true);
  }

  private finish(): void {
    this.phase = 'done';
    this.controls.setEnabled(false);
    this.tapZones.setVisible(false);
    this.rig.setMotion(0);
    this.rig.setPose('celebrate');
    AudioEngine.fanfare();
    this.vibrate(HAPTICS.finish);

    const standings: Standing[] = [
      { name: this.athlete.name, species: this.athlete.species, isPlayer: true, nationality: this.athlete.nationality, metric: this.elapsed },
      ...this.ai.map((a) => ({ name: a.name, species: a.species, isPlayer: false, nationality: a.nationality, metric: a.target })),
    ].sort((x, y) => x.metric - y.metric);
    const rank = standings.findIndex((s) => s.isPlayer) + 1;
    const score = Math.round(Phaser.Math.Clamp((45 - this.elapsed) / 23 * 1000, 0, 1000));
    const reward = getDiscipline('swim_50m').scoreToReward(score);

    this.bigText.setText(`${this.elapsed.toFixed(2)}"`).setColor('#ffd24a').setAlpha(1).setScale(0.6);
    this.tweens.add({ targets: this.bigText, scale: 1, duration: 500, ease: 'Back.out' });
    this.add.text(this.rig.container.x, this.waterY - 70, `${countryFlag(this.athlete.nationality)} ${this.elapsed.toFixed(2)}"`, {
      fontFamily: '"Twemoji Country Flags", system-ui', fontSize: `${Math.round(13 * this.uiS)}px`, color: '#0b1020', fontStyle: 'bold', backgroundColor: '#ffd24a', padding: { x: 6, y: 3 },
    }).setOrigin(0.5, 1).setDepth(9);

    const result: DisciplineResult = {
      disciplineId: 'swim_50m', metricKind: 'time', athleteId: this.athlete.id, athleteName: this.athlete.name,
      species: this.athlete.species, nationality: this.athlete.nationality, metric: this.elapsed, score, reward, rank, standings,
    };
    this.time.delayedCall(2400, () => this.finishDiscipline(result));
  }

  private vibrate(pattern: number | number[]): void {
    if (typeof navigator !== 'undefined' && 'vibrate' in navigator) {
      try { navigator.vibrate(pattern); } catch { /* unsupported */ }
    }
  }
}
